import { ActionTree } from 'vuex'
import {
    Contact,
    Institution,
    Insurance,
    Period,
    WidgetState
} from "@/apps/StudentExchangeNonPartnerWidget/models/store-viewmodel"
import { ILabels, ILanguage } from "@/apps/StudentExchangeNonPartnerWidget/models/config-viewmodel"
import services from "@/apps/StudentExchangeNonPartnerWidget/services"
import { AxiosError, AxiosResponse } from "axios"
import { progress } from "@/apps/StudentExchangeNonPartnerWidget/composables/progress"
import { ProgressAction, ProgressType } from "@/apps/StudentExchangeNonPartnerWidget/models/progress-enums"

export default <ActionTree<WidgetState, any>>{
    setLoading(context, payload: boolean): void {
        context.commit('loading', payload)
    },
    setLabels(context, payload: ILabels): void {
        context.commit('labels', payload)
    },
    setPeriod(context, payload: Period): void {
        context.commit('period', payload)
        context.dispatch('validateCompletion', ProgressType.PERIOD)
    },
    setContact(context, payload: Contact): void {
        context.commit('contact', payload)
        context.dispatch('validateCompletion', ProgressType.CONTACT)
    },
    setInstitution(context, payload: Institution): void {
        context.commit('institution', payload)
        context.dispatch('validateCompletion', ProgressType.INSTITUTION)
    },
    setInsurance(context, payload: Insurance): void {
        context.commit('insurance', payload)
        context.dispatch('validateCompletion', ProgressType.INSURANCE)
    },
    setLanguage(context, payload: ILanguage): void {
        context.commit('language', payload)
    },
    setErrorMessage(context, payload): void {
        context.commit('errorMessage', payload)
    },
    setProgramId(context, payload: number): void {
        context.commit('programId', payload)
    },
    setExchangeId(context, payload: number): void {
        context.commit('exchangeId', payload)
    },
    setAcademicYearId(context, payload: number): void {
        context.commit('academicYearId', payload)
    },
    validatePeriod(context, { period, labels }) {
        // converting dd-mm-yyyy to mm/dd/yyyy to support browser quirks
        let start = period.start.split("-")
        let end = period.end.split("-")
        start = new Date(`${start[1]}/${start[0]}/${start[2]}`)
        end =  new Date(`${end[1]}/${end[0]}/${end[2]}`)
        // end conversion 
        if (period.start && period.end && (start.getTime() >= end.getTime())) {
            context.commit('periodIsInvalid', true)
        } else {
            context.commit('periodIsInvalid', false)
            
            if (period.start && period.end && period.reason) {
                period.completed = true
                progress(labels.periodBlockTitle, ProgressAction.ADD)
            } else {
                period.completed = false
                progress(labels.periodBlockTitle, ProgressAction.REMOVE)
            }
        }
    },
    validateContact(context, { contact, labels }) {
        if (contact.name && contact.email) {
            const emailRegex = new RegExp(/^[A-Za-z0-9_!#$%&'*+/=?`{|}~^.-]+@[A-Za-z0-9.-]+$/, "gm");
            if (emailRegex.test(contact.email)) {
                context.commit('contactIsInvalid', false)
                contact.completed = true
                progress(labels.contactBlockTitle, ProgressAction.ADD)
            } else {
                context.commit('contactIsInvalid', true)
            }
        } else {
            contact.completed = false
            progress(labels.contactBlockTitle, ProgressAction.REMOVE)
        }
    },
    validateInstitution(context, { institution, labels }) {
        if (institution.name && institution.city && institution.country) {
            institution.completed = true
            progress(labels.nonPartnerBlockTitle, ProgressAction.ADD)
        } else {
            institution.completed = false
            progress(labels.nonPartnerBlockTitle, ProgressAction.REMOVE)
        }
    },
    validateInsurance(context, { insurance, labels }) {
        if (insurance.completed) {
            progress(labels.crisisManagementHealthInsuranceAgreementTitle, ProgressAction.ADD)
        } else {
            progress(labels.crisisManagementHealthInsuranceAgreementTitle, ProgressAction.REMOVE)
        }
    },
    validateCompletion(context, type: ProgressType): void {
        let getters = context.getters;
        switch (type) {
            case ProgressType.PERIOD:
                context.dispatch('validatePeriod', { period: getters.getPeriod, labels: getters.getLabels })
                break
            case ProgressType.CONTACT:
                context.dispatch('validateContact', { contact: getters.getContact, labels: getters.getLabels })
                break
            case ProgressType.INSTITUTION:
                context.dispatch('validateInstitution', { institution: getters.getInstitution, labels: getters.getLabels })
                break
            case ProgressType.INSURANCE:
                context.dispatch('validateInsurance', { insurance: getters.getInsurance, labels: getters.getLabels })
                break
        }
        context.dispatch('canContinue')
    },
    canContinue(context): void {
        let getters = context.getters;
        let canContinue = getters.getPeriod.completed
            && getters.getContact.completed
            && getters.getInstitution.completed
            && getters.getInsurance.completed
            && !getters.getLoading
        if (canContinue) {
            setTimeout(() => {
                progress(getters.getLabels.submitBlockTitle, ProgressAction.ADD)
            }, 200)
        } else {
            progress(getters.getLabels.submitBlockTitle, ProgressAction.REMOVE)
        }
        context.commit('canContinue', canContinue)
    },
    loadStudyName(context)
    {
        let getters = context.getters;
        services.studentStudy.getStudyName(getters.getLanguage, getters.getProgramId)
            .then((response: AxiosResponse) => {
                if (response.status === 204) {
                    context.commit('studyName', '')
                } else {
                    context.commit('studyName', response.data)
                }
            })
            .catch(() => {

            })
            .finally(() => {
                
            })
    },
    loadStudentExchangeNonPartner(context, exchangeId)
    {
        let getters = context.getters;
        if(!exchangeId) return
        services.studentExchange.getStudentExchangeNonPartner(getters.getLanguage, exchangeId)
            .then((response: AxiosResponse) => {
                if (response.status === 204) {
                    //
                } else {
                    setTimeout(() => {
                        context.dispatch('setPeriod', {
                            reason: response.data.selectedMobilityType,
                            start: response.data.fromDate,
                            end: response.data.toDate,
                        })
                    }, 200)
                    setTimeout(() => {
                        context.dispatch('setContact', {
                            name: response.data.contactName,
                            email: response.data.contactEmail
                        })
                    }, 400)
                    setTimeout(() => {
                        context.dispatch('setInstitution', {
                            name: response.data.partnerName,
                            city: response.data.partnerCity,
                            country: response.data.partnerCountry,
                        })
                    }, 600)
                    setTimeout(() => {
                        context.dispatch('setInsurance', {
                            agreement: response.data.agreeCrisisManagementInsurance,
                            completed: response.data.agreeCrisisManagementInsurance
                        })
                    }, 800)
                    
                    context.commit('editable', response.data.isEditable)
                    context.commit('cancelable', response.data.isCancelable)
                }
            })
            .catch(() => {

            })
            .finally(() => {

            })
    },
    addOrUpdateStudentExchangeNonPartner(context) {
        context.commit('loading', true)

        context.commit('isSubmitted', true)
        let getters = context.getters;
        services.studentExchange.postStudentExchangeNonPartner(getters.getState)
            .then((response: AxiosResponse) => {
                context.commit('isSubmitted', true)
            })
            .catch((error: AxiosError) => {
                context.commit('loading', false)
                context.commit('errorMessage', error.response?.data ? error.response.data : getters.getLabels.exchangeSubmissionErrorMessage)
            })
            .finally(() => {
                context.commit('loading', false)
            })
    }
}
